"use client";

import { notify, confirmDialog } from "@/components/ui/dialogs";
import PageHeader from "@/components/layout/PageHeader";
import SearchInput from "@/components/ui/search-input";
import { Avatar } from "@/components/crm/ListShell";
import { useState, useEffect, useCallback } from "react";
import {  
  Trash2, 
  Users as UsersIcon,
  Phone,
  Mail,
  Calendar,
  User,
  Save,
  Edit,
  Shield,
  Send} from "lucide-react";
import { get, put, del, post, can, getStoredUser } from "../utils/service";
import FloatingDrawer from "@/components/crm/FloatingDrawer";
import AppSelect from "@/components/ui/app-select";

/**
 * A row on this page: an organization member, or a pending invitation (kind = "invitation").
 * People are invited by email; they set their own password or sign in with Google / Microsoft.
 */
interface UserType {
  id: string;            // membership id, or invitation id
  kind: "member" | "invitation";
  user_id: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  avatar_url?: string | null;
  role_id: string;
  role_key: string;
  role_name: string;
  status: string;        // active | suspended | invited | expired
  last_login_at: string | null;
  teams: string[];
  created_at: string;
}

interface Role { id: string; key: string; name: string; memberships_count?: number }

interface ApiMember {
  id: string;
  user_id: string;
  status: string;
  created_at: string;
  joined_at: string | null;
  role: { id: string; key: string; name: string } | null;
  user: { id: string; first_name: string; last_name: string; email: string; phone: string | null; avatar_url?: string | null; last_login_at: string | null };
  teams: { id: string; name: string }[];
}

interface ApiInvitation {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  status: string;
  created_at: string;
  role: { id: string; key: string; name: string } | null;
}

interface PaginationState {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

const ROLE_COLORS: Record<string, string> = {
  owner: "bg-red-100 text-red-800",
  admin: "bg-orange-100 text-orange-800",
  sales_manager: "bg-purple-100 text-purple-800",
  finance_manager: "bg-purple-100 text-purple-800",
  project_manager: "bg-purple-100 text-purple-800",
};
const roleColor = (key: string) => ROLE_COLORS[key] ?? "bg-blue-100 text-blue-800";

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  suspended: "bg-red-100 text-red-800",
  pending: "bg-yellow-100 text-yellow-800",
  expired: "bg-gray-100 text-gray-800",
};
const statusLabel = (s: string) => ({ active: "Active", suspended: "Suspended", pending: "Invited", expired: "Invitation expired" } as Record<string, string>)[s] ?? s;

const apiMessage = (error: any, fallback: string) => {
  const errors = error?.response?.data?.errors as Record<string, string[]> | undefined;
  return (errors && Object.values(errors).flat()[0]) || error?.response?.data?.message || fallback;
};

const inputClass = "w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white";

const SkeletonRow = () => (
  <div className="animate-pulse p-6">
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded"></div>
  </div>
);


const AddUserForm = ({ 
  isOpen, 
  onClose,
  onSuccess,
  roles
}: { 
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  roles: Role[];
}) => {
  const defaultRole = () => roles.find((r) => r.key === "sales_user")?.id ?? roles.find((r) => r.key !== "owner")?.id ?? "";
  const [formData, setFormData] = useState({ first_name: "", last_name: "", email: "", role_id: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    if (isOpen) {
      setFormData({ first_name: "", last_name: "", email: "", role_id: defaultRole() });
      setErrorText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, roles]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorText("");

    try {
      await post("/invitations", formData);
      onSuccess();
      onClose();
    } catch (error) {
      setErrorText(apiMessage(error, "Could not send the invitation."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <FloatingDrawer
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Invite a Team Member</h2>
          <p className="text-xs text-gray-400 mt-1">
            They'll get an email to join. They can set a password or sign in with Google or Microsoft.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">First Name</label>
            <input type="text" value={formData.first_name} onChange={(e) => handleChange("first_name", e.target.value)} className={inputClass} />
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Last Name</label>
            <input type="text" value={formData.last_name} onChange={(e) => handleChange("last_name", e.target.value)} className={inputClass} />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Email *</label>
          <input type="email" required value={formData.email} onChange={(e) => handleChange("email", e.target.value)} className={inputClass} />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Role *</label>
          <AppSelect value={formData.role_id} onChange={(e) => handleChange("role_id", e.target.value)} className={inputClass}>
            {roles.map(role => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </AppSelect>
        </div>
      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        {errorText && <p className="text-xs text-red-400">{errorText}</p>}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !formData.email || !formData.role_id}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Sending invitation...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Send Invitation
            </>
          )}
        </button>
        
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

/** What an administrator can change about a member: role and access. Profiles are edited by the person. */
const EditUserForm = ({ 
  isOpen, 
  onClose,
  onSuccess,
  user,
  roles
}: { 
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: UserType | null;
  roles: Role[];
}) => {
  const [formData, setFormData] = useState({ role_id: "", status: "active" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    if (user) {
      setFormData({ role_id: user.role_id, status: user.status === "suspended" ? "suspended" : "active" });
      setErrorText("");
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    setErrorText("");

    try {
      await put(`/members/${user.id}`, formData);
      onSuccess();
      onClose();
    } catch (error) {
      setErrorText(apiMessage(error, "Could not update this member."));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user || user.kind !== "member") return null;

  return (
    <FloatingDrawer
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Edit Member</h2>
          <p className="text-xs text-gray-400 mt-1">{user.first_name} {user.last_name} · {user.email}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Role</label>
          <AppSelect value={formData.role_id} onChange={(e) => setFormData((f) => ({ ...f, role_id: e.target.value }))} className={inputClass}>
            {roles.map(role => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </AppSelect>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Access</label>
          <AppSelect value={formData.status} onChange={(e) => setFormData((f) => ({ ...f, status: e.target.value }))} className={inputClass}>
            <option value="active">Active</option>
            <option value="suspended">Suspended (cannot access this organization)</option>
          </AppSelect>
        </div>

        <p className="text-[11px] text-gray-500">Name, phone and sign-in methods are managed by the member in their own settings.</p>
      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        {errorText && <p className="text-xs text-red-400">{errorText}</p>}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Update Member
            </>
          )}
        </button>
        
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const UserDrawer = ({ 
  user, 
  isOpen, 
  onClose,
  onDelete,
  onEdit,
  onResend,
  canManage
}: { 
  user: UserType | null;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (user: UserType) => void;
  onEdit: () => void;
  onResend: (user: UserType) => void;
  canManage: boolean;
}) => {
  if (!user) return null;
  const invitation = user.kind === "invitation";

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">{invitation ? "Invitation" : "Member Details"}</h2>
          <p className="text-xs text-gray-400 mt-1">{[user.first_name, user.last_name].filter(Boolean).join(" ") || user.email}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Role</label>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${roleColor(user.role_key)}`}>
              {user.role_name}
            </span>
          </div>
          
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Status</label>
            <div className={`px-3 py-1 text-xs font-semibold rounded-lg ${STATUS_COLORS[user.status] ?? "bg-gray-100 text-gray-800"}`}>
              {statusLabel(user.status)}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Full Name</p>
              <p className="text-xs text-gray-400">{[user.first_name, user.last_name].filter(Boolean).join(" ") || "—"}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Email</p>
              <p className="text-xs text-gray-400">{user.email}</p>
            </div>
          </div>

          {user.phone && (
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Phone</p>
                <p className="text-xs text-gray-400">{user.phone}</p>
              </div>
            </div>
          )}

          {user.teams.length > 0 && (
            <div className="flex items-center gap-3">
              <Shield className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Teams</p>
                <p className="text-xs text-gray-400">{user.teams.join(", ")}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">{invitation ? "Invited" : "Joined"}</p>
              <p className="text-xs text-gray-400">
                {new Date(user.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          {!invitation && (
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Last Sign-in</p>
                <p className="text-xs text-gray-400">{user.last_login_at ? new Date(user.last_login_at).toLocaleString() : "Never"}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {canManage && (
        <div className="p-3 border-t border-gray-800">
          <div className="grid grid-cols-2 gap-2">
            {invitation ? (
              <button
                onClick={() => onResend(user)}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Send className="w-4 h-4" />
                Resend
              </button>
            ) : (
              <button
                onClick={onEdit}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
            )}
            
            <button
              onClick={() => onDelete(user)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              {invitation ? "Revoke" : "Remove"}
            </button>
          </div>
        </div>
      )}
    </FloatingDrawer>
  );
};


function UsersPage() {
  const canInvite = can("members.invite");
  const canUpdate = can("members.update");
  const canRemove = can("members.remove");
  const me = getStoredUser();
  const [allUsers, setAllUsers] = useState<UserType[]>([]);
  const [search, setSearch] = useState("");
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [showUserDrawer, setShowUserDrawer] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [members, invitations] = await Promise.all([
        get<{ data: { members: ApiMember[] } }>("/members"),
        canInvite ? get<{ data: { invitations: ApiInvitation[] } }>("/invitations") : Promise.resolve({ data: { invitations: [] as ApiInvitation[] } }),
      ]);
      const rows: UserType[] = [
        ...invitations.data.invitations.map((i): UserType => ({
          id: i.id, kind: "invitation", user_id: null, first_name: i.first_name ?? "", last_name: i.last_name ?? "", email: i.email, phone: "",
          role_id: i.role?.id ?? "", role_key: i.role?.key ?? "", role_name: i.role?.name ?? "—", status: i.status, last_login_at: null, teams: [], created_at: i.created_at,
        })),
        ...members.data.members.map((m): UserType => ({
          id: m.id, kind: "member", user_id: m.user_id, first_name: m.user.first_name, last_name: m.user.last_name, email: m.user.email, phone: m.user.phone ?? "", avatar_url: m.user.avatar_url ?? null,
          role_id: m.role?.id ?? "", role_key: m.role?.key ?? "", role_name: m.role?.name ?? "—", status: m.status, last_login_at: m.user.last_login_at,
          teams: m.teams.map((t) => t.name), created_at: m.joined_at ?? m.created_at,
        })),
      ];
      setAllUsers(rows);
      setPagination((prev) => ({ ...prev, total: rows.length, total_pages: Math.ceil(rows.length / prev.per_page) }));
    } catch (error) {
      notify(apiMessage(error, "Could not load the team."));
    } finally {
      setLoading(false);
    }
  }, [canInvite]);

  useEffect(() => {
    fetchUsers();
    if (canInvite || canUpdate) {
      get<{ data: { roles: Role[] } }>("/roles").then((r) => setRoles(r.data.roles)).catch(() => setRoles([]));
    }
  }, [fetchUsers, canInvite, canUpdate]);

  const term = search.trim().toLowerCase();
  const matching = term
    ? allUsers.filter((u) => [u.first_name, u.last_name, u.email, u.phone, u.role_name, ...u.teams].some((v) => (v ?? "").toLowerCase().includes(term)))
    : allUsers;
  const paged = { ...pagination, total: matching.length, total_pages: Math.ceil(matching.length / pagination.per_page) };
  const users = matching.slice((pagination.page - 1) * pagination.per_page, pagination.page * pagination.per_page);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeOne = async (user: UserType) => {
    if (user.kind === "invitation") await del(`/invitations/${user.id}`);
    else await del(`/members/${user.id}`);
  };

  const handleDelete = async (user: UserType) => {
    const question = user.kind === "invitation"
      ? `Revoke the invitation sent to ${user.email}?`
      : `Remove ${user.first_name} ${user.last_name} from this organization? Their records stay; they lose access.`;
    if (!await confirmDialog(question)) return;

    try {
      await removeOne(user);
      if (selectedUser?.id === user.id) {
        setShowUserDrawer(false);
        setSelectedUser(null);
      }
      notify(user.kind === "invitation" ? "Invitation revoked" : "Member removed");
      fetchUsers();
    } catch (error) {
      notify(apiMessage(error, "Could not remove this person."));
    }
  };

  const handleResend = async (user: UserType) => {
    try {
      await post(`/invitations/${user.id}/resend`, {});
      notify(`Invitation sent again to ${user.email}`);
      fetchUsers();
    } catch (error) {
      notify(apiMessage(error, "Could not resend the invitation."));
    }
  };

  const handleViewUser = (user: UserType) => {
    setSelectedUser(user);
    setShowUserDrawer(true);
  };

  const handleAddSuccess = () => {
    fetchUsers();
    notify("Invitation sent");
  };

  const handleEditSuccess = () => {
    fetchUsers();
    notify("Member updated");
  };


  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Team" description="Members of this organization, their roles and pending invitations">
          {canInvite && (
            <button
                onClick={() => setShowAddUser(true)}
                className="px-5 py-1.5 bg-gradient-to-r from-primary to-primary/90 text-white text-sm font-light tracking-wide rounded-full hover:from-black/90 hover:to-black transition-all duration-200  min-w-[140px]"
              >
                Invite Member
              </button>
          )}
        </PageHeader>

        <div className="mb-4">
          <SearchInput className="w-full sm:w-72" placeholder="Search name, email, role or team…" value={search}
            onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }} />
        </div>

        {/* Table Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <SkeletonRow />
          ) : (
            <>
              {users.length > 0 && (
                <div className="grid gap-4 p-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {users.map((u) => {
                    const name = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email;
                    const invite = u.kind === "invitation";
                    return (
                      <div
                        key={`${u.kind}-${u.id}`}
                        onClick={() => handleViewUser(u)}
                        className={`flex cursor-pointer flex-col rounded-xl border bg-white p-4 transition hover:border-primary/40 hover:shadow-sm ${invite ? "border-dashed border-gray-300" : "border-gray-200"}`}
                      >
                        <div className="flex items-start gap-3">
                          <Avatar name={invite ? u.email : name} src={u.avatar_url} />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-gray-900">{name}{u.user_id && u.user_id === me?.id && <span className="ml-1 text-xs font-normal text-gray-400">(you)</span>}</p>
                            <p className="truncate text-xs text-gray-500">{u.email}</p>
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[u.status] ?? "bg-gray-100 text-gray-800"}`}>{statusLabel(u.status)}</span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roleColor(u.role_key)}`}>{u.role_name}</span>
                          {u.teams.map((t) => <span key={t} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{t}</span>)}
                        </div>
                        <div className="min-h-[1rem] flex-1" />
                        <div className="flex items-center justify-between border-t border-gray-100 pt-3 text-xs text-gray-500">
                          <span>{invite ? `Invited ${new Date(u.created_at).toLocaleDateString()}` : `Since ${new Date(u.created_at).toLocaleDateString()}`}</span>
                          {!invite && <span>{u.last_login_at ? `Active ${new Date(u.last_login_at).toLocaleDateString()}` : "Never signed in"}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {users.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <UsersIcon className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-light text-gray-900 mb-2">No members yet</h3>
                  <p className="text-sm font-light text-gray-500 mb-6 max-w-md mx-auto">
                    Invite your colleagues to work in this organization.
                  </p>
                  {canInvite && (
                    <button
                      type="button"
                      onClick={() => setShowAddUser(true)}
                      className="px-6 py-2 bg-primary text-white text-sm font-light rounded-lg hover:bg-primary/90 transition-all duration-200"
                    >
                      Invite First Member
                    </button>
                  )}
                </div>
              )}

              {paged.total_pages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-xs text-gray-600">
                    Showing{" "}
                    {((pagination.page - 1) * pagination.per_page) + 1}–
                    {Math.min(pagination.page * pagination.per_page, paged.total)}{" "}
                    of {paged.total} people
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-label="Previous page"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <span className="px-2 py-1 text-xs text-gray-600">
                      Page {pagination.page} of {paged.total_pages}
                    </span>
                    <button
                      type="button"
                      aria-label="Next page"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === paged.total_pages}
                      className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <UserDrawer
        user={selectedUser}
        isOpen={showUserDrawer}
        canManage={selectedUser?.kind === "invitation" ? canInvite : canUpdate || canRemove}
        onClose={() => {
          setShowUserDrawer(false);
          setSelectedUser(null);
        }}
        onDelete={handleDelete}
        onResend={handleResend}
        onEdit={() => {
          setShowUserDrawer(false);
          setShowEditUser(true);
        }}
      />

      <AddUserForm
        isOpen={showAddUser}
        roles={roles}
        onClose={() => setShowAddUser(false)}
        onSuccess={handleAddSuccess}
      />

      <EditUserForm
        isOpen={showEditUser}
        roles={roles}
        onClose={() => {
          setShowEditUser(false);
          setSelectedUser(null);
        }}
        onSuccess={handleEditSuccess}
        user={selectedUser}
      />
    </div>
  );
}

export default UsersPage;
